import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Surface, Empty } from "../components/Primitives";
import { Plus, ArrowDown, ArrowUp } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function StockMovements() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "user";
  const [moves, setMoves] = useState([]);
  const [products, setProducts] = useState([]);
  const [sites, setSites] = useState([]);
  const [stock, setStock] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ product_id:"", site_id:"", type:"entree", quantity:1, reference:"", note:"" });

  const load = async () => {
    const [mv, p, s, st] = await Promise.all([api.get("/stock/movements"), api.get("/products"), api.get("/sites"), api.get("/stock")]);
    setMoves(mv.data); setProducts(p.data); setSites(s.data); setStock(st.data);
  };
  useEffect(()=>{load();},[]);

  const productMap = useMemo(()=>Object.fromEntries(products.map(p=>[p.id, p])), [products]);
  const siteMap = useMemo(()=>Object.fromEntries(sites.map(s=>[s.id, s])), [sites]);

  const submit = async e => {
    e.preventDefault();
    try {
      await api.post("/stock/movements", { ...form, quantity: Number(form.quantity) });
      toast.success("Mouvement enregistré");
      setShow(false);
      setForm({ product_id:"", site_id:"", type:"entree", quantity:1, reference:"", note:"" });
      load();
    } catch(e){ toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Mouvements de stock" subtitle="Entrées · Sorties · Ajustements"
        actions={canEdit && <button data-testid="new-movement-btn" className="btn-primary flex items-center gap-2" onClick={()=>setShow(!show)}><Plus size={14}/>Nouveau mouvement</button>}
      />

      {show && canEdit && (
        <Surface title="Enregistrer un mouvement">
          <form onSubmit={submit} className="p-5 grid grid-cols-2 md:grid-cols-3 gap-4">
            <div><label className="label-mono block mb-1.5">Produit</label>
              <select data-testid="mvt-product" required className="input-base" value={form.product_id} onChange={e=>setForm({...form,product_id:e.target.value})}>
                <option value="">— Choisir —</option>
                {products.map(p => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
              </select>
            </div>
            <div><label className="label-mono block mb-1.5">Site</label>
              <select data-testid="mvt-site" required className="input-base" value={form.site_id} onChange={e=>setForm({...form,site_id:e.target.value})}>
                <option value="">— Choisir —</option>
                {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div><label className="label-mono block mb-1.5">Type</label>
              <select data-testid="mvt-type" className="input-base" value={form.type} onChange={e=>setForm({...form,type:e.target.value})}>
                <option value="entree">Entrée</option>
                <option value="sortie">Sortie</option>
                <option value="ajustement">Ajustement</option>
              </select>
            </div>
            <div><label className="label-mono block mb-1.5">Quantité</label><input data-testid="mvt-qty" type="number" required min="1" className="input-base" value={form.quantity} onChange={e=>setForm({...form,quantity:e.target.value})}/></div>
            <div><label className="label-mono block mb-1.5">Référence</label><input className="input-base" value={form.reference} onChange={e=>setForm({...form,reference:e.target.value})}/></div>
            <div><label className="label-mono block mb-1.5">Note</label><input className="input-base" value={form.note} onChange={e=>setForm({...form,note:e.target.value})}/></div>
            <div className="col-span-full flex gap-2"><button data-testid="submit-mvt" type="submit" className="btn-primary">Enregistrer</button><button type="button" className="btn-ghost" onClick={()=>setShow(false)}>Annuler</button></div>
          </form>
        </Surface>
      )}

      <Surface title="Stock actuel par site" testId="current-stock">
        {stock.length === 0 ? <Empty message="Aucun stock"/> : (
          <table className="data-table">
            <thead><tr><th>Produit</th><th>Site</th><th>Quantité</th><th>Statut</th></tr></thead>
            <tbody>
              {stock.map((s, i) => {
                const p = productMap[s.product_id]; const min = p?.min_stock || 10;
                const low = s.quantity <= min;
                return (
                  <tr key={`${s.product_id}-${s.site_id}-${i}`}>
                    <td className="font-medium">{p?.name || s.product_id}</td>
                    <td className="text-[var(--text-muted)]">{siteMap[s.site_id]?.name || s.site_id}</td>
                    <td className={`font-data ${low ? "neon-text-pink" : "neon-text-cyan"}`}>{s.quantity}</td>
                    <td>{low ? <span className="badge-alert">BAS</span> : <span className="badge-success">OK</span>}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Surface>

      <Surface title="Historique des mouvements">
        {moves.length === 0 ? <Empty/> : (
          <table className="data-table">
            <thead><tr><th>Date</th><th>Type</th><th>Produit</th><th>Site</th><th>Qté</th><th>Référence</th></tr></thead>
            <tbody>
              {moves.map(m => (
                <tr key={m.id}>
                  <td className="font-data text-[var(--text-muted)] text-xs">{m.created_at?.slice(0,16).replace("T"," ")}</td>
                  <td>
                    {m.type === "entree" ? <span className="badge-success flex items-center gap-1 w-fit"><ArrowDown size={10}/>Entrée</span> :
                     m.type === "sortie" ? <span className="badge-alert flex items-center gap-1 w-fit"><ArrowUp size={10}/>Sortie</span> :
                     <span className="badge-warn">Ajust.</span>}
                  </td>
                  <td>{productMap[m.product_id]?.name || m.product_id}</td>
                  <td className="text-[var(--text-muted)]">{siteMap[m.site_id]?.name || m.site_id}</td>
                  <td className="font-data">{m.quantity}</td>
                  <td className="font-data text-[var(--text-muted)] text-xs">{m.reference || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Surface>
    </div>
  );
}
